// Centralized configuration and constants for terrain generation and rendering

// Grid/world dimensions
export const GRID_SIZE = 48; // number of blocks along X and Z
export const WORLD_HEIGHT = 36;

// Block size (world units per block)
export const BLOCK_SIZE = 1;

// Ground composition
export const BASE_GROUND_OFFSET = 8;
export const DIRT_LAYER_THICKNESS = 3;
export const MIN_STONE_TOP_ABS = 1;
export const BEDROCK_Y = 0;
export const DIRT_BUFFER_BELOW_TOP = 1;
// How many cells of stone/dirt remain visible BELOW the lowest column's
// surface. Anything below that gets cleared so the rendered chunk reads as a
// floating slab instead of a tall dark pillar (helps integration with light/
// dark page backgrounds where the deep base mass would otherwise dominate).
export const VISIBLE_BASE_DEPTH = 2;

// Heightmap generation (FBM)
export const HEIGHT_MIN = 4;
export const HEIGHT_MAX = 12;
export const NOISE_SCALE = 0.02; // base frequency for octave 0
export const FBM_OCTAVES = 3;
export const FBM_LACUNARITY = 2.0;
export const FBM_GAIN = 0.5;
export const HEIGHT_EASING_ITERATIONS = 1; // FBM already adds detail
export const SMOOTHING_PASSES = 1;
export const DOMAIN_WARP_AMP = 4; // in world units; 0 disables warping
export const DOMAIN_WARP_FREQ = 0.05;

// Erosion
export const TALUS_THRESHOLD = 2;
export const TALUS_PASSES = 1;

// River generation
export const RIVER_OCCURRENCE_MOD = 4;
export const RIVER_SEED_OFFSET = 9999;
export const RIVER_NOISE_Z_FREQ = 0.05;
export const RIVER_CENTER_X_AMPLITUDE = 8;
export const RIVER_HALF_THICKNESS = 1;
export const RIVER_DEPTH = 3;

// Snow & biomes
export const SNOW_MIN_HEIGHT_REL = 8;
export const SNOW_HEIGHT_FACTOR = 0.1;

// Biome generation (0..1 normalized fields)
export const BIOME_NOISE_SCALE_TEMP = 0.035;
export const BIOME_NOISE_SCALE_HUMID = 0.04;
// Smoothing pass kills 1-cell biome speckle from raw noise.
export const BIOME_SMOOTH_PASSES = 1;
// Cold breakpoints for temp axis.
export const VERY_COLD_TEMP_THRESHOLD = 0.22;
export const COLD_TEMP_THRESHOLD = 0.38;
// Hot breakpoint splits desert/savanna from temperate band.
export const HOT_TEMP_THRESHOLD = 0.7;
// Humidity axis breakpoints (per temperature band).
export const HUMID_THRESHOLD = 0.62;
export const DRY_THRESHOLD = 0.32;

// Desert specifics
export const DESERT_SANDSTONE_DEPTH = 2;

export const MESA_STRATA_DEPTH = 9;

export const MUSHROOM_RARITY = 30;

export const PORTAL_RARITY = 10;
export const NETHER_SEED_OFFSET = 6660;
export const NETHER_LAVA_HALF_THICKNESS = 2;
export const NETHER_GLOWSTONE_CLUSTERS = 4;

// Decorations
export const DECORATION_NOISE_SCALE = 0.18;

// Caves: seeded ellipsoidal rooms + occasional tunnel between two of them.
export const CAVE_ENABLED = true;
export const CAVE_MIN_DEPTH_BELOW_TOP = 3;   // never carve within N blocks of surface
export const CAVE_MIN_Y_ABOVE_BEDROCK = 1;   // never carve into bedrock layer
export const CAVE_COUNT_MIN = 2;             // rooms per render (lower bound)
export const CAVE_COUNT_MAX = 3;             // rooms per render (upper bound)
export const CAVE_RADIUS_MIN = 3;            // ellipsoid horizontal radius (cells)
export const CAVE_RADIUS_MAX = 5;
export const CAVE_VRADIUS_MIN = 2;           // ellipsoid vertical radius (cells)
export const CAVE_VRADIUS_MAX = 3;
export const CAVE_TUNNEL_PROBABILITY = 0.6;  // chance to connect two rooms with a tunnel
export const CAVE_TUNNEL_RADIUS = 1.4;       // sphere brush radius along tunnel path
// Always carve at least one tunnel from the first room to the chunk edge so a
// cave mouth is visible from the outside in the isometric view.
export const CAVE_ENTRANCE_RADIUS = 2.4;

// Surface structures (one per render, picked by biome).
export const STRUCTURES_ENABLED = true;

// Color variation
export const COLOR_JITTER_DEFAULT = 0.04;

export const AO_STRENGTH = 0.35;

export const PORTAL_FLOOD_MS = 620;
export const PORTAL_DRAIN_MS = 780;

// Camera/frame utils
export const FRAME_FRUSTUM_SCALE = 1.6;
export const CAMERA_NEAR = -1000;
export const CAMERA_FAR = 1000;
export const CAMERA_OFFSET_DIR: [number, number, number] = [1, 1, 1];

// Scene/renderer defaults
export const DEFAULT_ORTHO_FRUSTUM = 6;
export const AMBIENT_INTENSITY = 1.15;
export const SUN_INTENSITY = 1.25;
export const SUN_POSITION: [number, number, number] = [3, 5, 2];
export const MIN_RENDERER_SIZE = 400;
export const RENDERER_MAX_PIXEL_RATIO = 2;
